import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, Image as ImageIcon, LayoutDashboard, Star } from 'lucide-react';
import api from '../api/client';
import { useReveal } from '../hooks/useInView';
import { useReferralCta } from '../hooks/useReferralCta';
import { useAuthStatus } from '../hooks/useAuthStatus';

// Fallback content used when the API is unreachable (offline marketing preview,
// dev without backend, etc.). Matches PRD §5 dosage table.
const FALLBACK_PLANS = [
  {
    _id: 'fallback-starter',
    name: 'Starter',
    description: 'Try the protocol with one full bottle. Best for first-time users.',
    price: 1280,
    currency: 'MYR',
    bottles: 1,
    weeks: 4,
    expectedLoss: '2 – 4 kg',
    ribbon: null,
    features: [
      '1 bottle (20 mg · 4 injections)',
      'Weekly clinic visit for 4 weeks',
      'Doctor consultation included',
      'Progress tracking dashboard'
    ]
  },
  {
    _id: 'fallback-standard',
    name: 'Standard',
    description: 'The most popular plan. Meaningful results in two months.',
    price: 2380,
    currency: 'MYR',
    bottles: 2,
    weeks: 8,
    expectedLoss: '5 – 8 kg',
    ribbon: 'Most chosen',
    features: [
      '2 bottles (40 mg · 8 injections)',
      '8 weeks of supervised treatment',
      'Doctor consultation + side-effect review',
      'AI dose-timing reminders',
      'Lab panel suggestion (HbA1c, lipid)'
    ]
  },
  {
    _id: 'fallback-extended',
    name: 'Extended',
    description: 'A full 12-week course for users with higher targets.',
    price: 3380,
    currency: 'MYR',
    bottles: 3,
    weeks: 12,
    expectedLoss: '9 – 13 kg',
    ribbon: 'Best value',
    features: [
      '3 bottles (60 mg · 12 injections)',
      '12 weeks of supervised treatment',
      'Doctor consultation + 2 follow-ups',
      'Full lab panel (HbA1c, liver, kidney, lipid)',
      'Maintenance plan after course'
    ]
  }
];

const API_HOST = (api?.defaults?.baseURL || '').replace(/\/api\/v1\/?$/, '');

function bannerSrc(pkg) {
  if (!pkg?.bannerUrl) return '';
  return `${API_HOST}${pkg.bannerUrl}?v=${pkg.updatedAt || ''}`;
}

// PRD bottles → standard duration / expected loss.
function dosageOf(bottles) {
  switch (Number(bottles)) {
    case 1: return { weeks: 4,  expectedLoss: '2 – 4 kg' };
    case 2: return { weeks: 8,  expectedLoss: '5 – 8 kg' };
    case 3: return { weeks: 12, expectedLoss: '9 – 13 kg' };
    default: return { weeks: null, expectedLoss: null };
  }
}

function PlanCard({ pkg, featured }) {
  const r = useReveal({ threshold: 0.1 });
  const { ctaUrl } = useReferralCta();
  const authed = useAuthStatus();
  const banner = bannerSrc(pkg);
  const intakeHref = pkg.bottles ? `/intake?bottles=${pkg.bottles}` : '/intake';
  const dose = dosageOf(pkg.bottles);
  const weeks = pkg.weeks ?? dose.weeks ?? pkg.durationWeeks;
  const expectedLoss = pkg.expectedLoss
    || (pkg.expectedLossKg?.min != null && pkg.expectedLossKg?.max != null
        ? `${pkg.expectedLossKg.min} – ${pkg.expectedLossKg.max} kg`
        : dose.expectedLoss);
  const bottles = pkg.bottles;
  const price = pkg.price;
  const currency = pkg.currency || 'MYR';

  return (
    <article
      ref={r.ref}
      className={[
        r.className,
        'relative rounded-3xl flex flex-col bg-white transition-all duration-300',
        featured
          ? 'border-2 border-brand-700 shadow-card md:-translate-y-2 z-10'
          : 'border border-ink-200 shadow-soft hover:-translate-y-1 hover:shadow-card'
      ].join(' ')}
    >
      {/* Floating "Most popular" badge for the featured plan */}
      {featured && (
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-20">
          <span className="inline-flex items-center gap-1.5 bg-brand-700 text-white px-3.5 py-1.5 rounded-full text-[10px] font-semibold tracking-[0.18em] uppercase shadow-card whitespace-nowrap">
            <Star size={11} fill="currentColor" strokeWidth={0} /> Most popular
          </span>
        </div>
      )}

      {/* Secondary ribbon (e.g. "Best value") for non-featured cards */}
      {pkg.ribbon && !featured && (
        <span className="absolute top-5 right-5 z-10 text-[10px] font-semibold tracking-[0.18em] uppercase px-2.5 py-1 rounded-full bg-accent-50 text-accent-700 border border-accent-200">
          {pkg.ribbon}
        </span>
      )}

      {/* Product image tile — neutral, soft, premium */}
      <div className="p-4">
        <div
          className="relative w-full overflow-hidden flex items-center justify-center"
          style={{
            height: 200,
            borderRadius: 20,
            background: 'linear-gradient(180deg, #F4EFF8 0%, #EFE5F5 100%)',
          }}
        >
          {banner ? (
            <img
              src={banner}
              alt={`${pkg.name} banner`}
              className="max-h-full max-w-full object-contain p-4"
            />
          ) : (
            <ImageIcon size={40} className="text-brand-300" />
          )}
        </div>
      </div>

      <div className="px-6 sm:px-7 pb-7 flex flex-col flex-1">
        {/* Header */}
        <h3 className="text-2xl font-semibold tracking-tight text-ink-900">
          {pkg.name}
        </h3>
        {pkg.description && (
          <p className="mt-2 text-sm text-ink-500 leading-relaxed">
            {pkg.description}
          </p>
        )}

        {/* Inline stat row with bullet separators */}
        {(bottles || weeks || expectedLoss) && (
          <div className="mt-5 flex items-center gap-2.5 text-[13px] text-ink-700 font-medium">
            <span>{bottles ?? '—'} {bottles === 1 ? 'bottle' : 'bottles'}</span>
            <span className="w-1 h-1 rounded-full bg-ink-300" />
            <span>{weeks ?? '—'} weeks</span>
            <span className="w-1 h-1 rounded-full bg-ink-300" />
            <span>{expectedLoss ?? '—'}</span>
          </div>
        )}

        {/* Price */}
        <div className="mt-6 pb-6 border-b border-ink-100">
          <div className="flex items-baseline gap-1.5 flex-wrap">
            <span className="text-sm font-medium text-ink-500">From</span>
            <span className="text-sm font-medium text-ink-500">{currency}</span>
            <span className="text-4xl font-semibold tracking-tight text-ink-900 tabular-nums">
              {price?.toLocaleString?.() || price}
            </span>
          </div>
          <div className="mt-1.5 text-xs text-ink-500">
            Per course · Final price confirmed by clinic
          </div>
        </div>

        {/* Feature list with circular check icons */}
        {pkg.features?.length > 0 && (
          <ul className="mt-6 space-y-3 text-sm text-ink-700">
            {pkg.features.map((t) => (
              <li key={t} className="flex items-start gap-2.5">
                <span className="shrink-0 mt-0.5 w-5 h-5 rounded-full bg-brand-50 text-brand-700 flex items-center justify-center">
                  <Check size={11} strokeWidth={3} />
                </span>
                <span className="leading-snug">{t}</span>
              </li>
            ))}
          </ul>
        )}

        {/* CTA — bottom-anchored so cards line up */}
        <div className="mt-auto pt-7">
          {authed ? (
            <a
              href={ctaUrl}
              className={[
                'inline-flex items-center gap-2 font-semibold rounded-full px-5 py-3.5 text-sm w-full justify-center transition-colors',
                featured
                  ? 'bg-brand-700 text-white hover:bg-brand-800'
                  : 'bg-ink-900 text-white hover:bg-ink-800'
              ].join(' ')}
            >
              <LayoutDashboard size={14} /> Open my dashboard
            </a>
          ) : (
            <Link
              to={intakeHref}
              className={[
                'inline-flex items-center gap-2 font-semibold rounded-full px-5 py-3.5 text-sm w-full justify-center transition-colors',
                featured
                  ? 'bg-brand-700 text-white hover:bg-brand-800'
                  : 'bg-ink-900 text-white hover:bg-ink-800'
              ].join(' ')}
            >
              Get started <ArrowRight size={16} />
            </Link>
          )}
        </div>
      </div>
    </article>
  );
}

export default function Packages() {
  const head = useReveal();
  const { ctaUrl } = useReferralCta();
  const authed = useAuthStatus();
  const [items, setItems] = useState([]);
  const [usingFallback, setUsingFallback] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get('/packages', { params: { active: 'true' } })
      .then((res) => {
        const data = res?.data?.data ?? res?.data ?? [];
        if (cancelled) return;
        const active = (data || []).filter((p) => p.isActive);
        if (active.length > 0) {
          setItems(active);
          setUsingFallback(false);
        } else {
          setItems(FALLBACK_PLANS);
          setUsingFallback(true);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setItems(FALLBACK_PLANS);
        setUsingFallback(true);
      });
    return () => { cancelled = true; };
  }, []);

  // Highlight the middle card (or one tagged "Most chosen") as featured.
  const featuredIndex = items.findIndex((p) => p.ribbon === 'Most chosen');
  const fIndex = featuredIndex >= 0 ? featuredIndex : Math.floor(items.length / 2);

  return (
    <section id="packages" className="section bg-ivory">
      <div className="container-x">
        <div ref={head.ref} className={`${head.className} max-w-2xl mx-auto text-center`}>
          <span className="eyebrow">Treatment plans</span>
          <h2 className="h-section mt-4">Choose your plan. We'll match the clinic.</h2>
          <p className="lead mt-4">
            Every plan includes doctor consultation, supervised weekly visits, and progress
            tracking. Your clinic confirms final dosage and price after the eligibility quiz.
          </p>
        </div>

        <div className="mt-16 pt-2 grid md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
          {items.map((p, i) => (
            <PlanCard key={p._id || p.name} pkg={p} featured={i === fIndex} />
          ))}
        </div>

        <div className="mt-12 text-center">
          {authed ? (
            <a
              href={ctaUrl}
              className="inline-flex items-center gap-2 text-sm font-medium text-ink-700 hover:text-brand-700 transition-colors"
            >
              <LayoutDashboard size={14} /> Open my dashboard <ArrowRight size={14} />
            </a>
          ) : (
            <Link
              to="/intake"
              className="inline-flex items-center gap-2 text-sm font-medium text-ink-700 hover:text-brand-700 transition-colors"
            >
              Not sure which plan? Take the 2-minute eligibility quiz <ArrowRight size={14} />
            </Link>
          )}
        </div>

        <p className="mt-6 text-center text-[11px] text-ink-400 max-w-2xl mx-auto leading-relaxed">
          {usingFallback ? 'Showing sample plans. ' : ''}
          Prices in MYR. Standard course is one injection per week. Side-effects and
          contraindications confirmed at the clinic by an MMC-registered doctor with a valid APC.
        </p>
      </div>
    </section>
  );
}
